import type { IAdminNotificationRepository } from '@yawi/domain';
import type {
  TenantAdminRecipient,
  SendNotificationDto,
  SendBulkNotificationDto,
  BroadcastNotificationDto,
  NotificationSendResult,
  PaginatedNotifications,
  MasterNotification,
  UnreadCountResponse,
  MarkAllReadResponse,
} from '@yawi/types';
import { getApiClient } from '../../http/apiClient';

const BASE_URL = 'v1/admin/notifications';

export class ApiAdminNotificationRepository implements IAdminNotificationRepository {
  // ============ Sending Notifications ============
  async getTenantAdmins(tenantId?: string): Promise<TenantAdminRecipient[]> {
    const client = getApiClient();
    const url = tenantId ? `${BASE_URL}/tenant-admins?tenantId=${tenantId}` : `${BASE_URL}/tenant-admins`;
    const response = await client.get<TenantAdminRecipient[]>(url);
    return response;
  }

  async send(data: SendNotificationDto): Promise<NotificationSendResult> {
    const client = getApiClient();
    const response = await client.post<NotificationSendResult>(`${BASE_URL}/send`, data);
    return response;
  }

  async sendBulk(data: SendBulkNotificationDto): Promise<NotificationSendResult> {
    const client = getApiClient();
    const response = await client.post<NotificationSendResult>(`${BASE_URL}/send-bulk`, data);
    return response;
  }

  async sendToTenant(tenantId: string, data: BroadcastNotificationDto): Promise<NotificationSendResult> {
    const client = getApiClient();
    const response = await client.post<NotificationSendResult>(`${BASE_URL}/send-to-tenant/${tenantId}`, data);
    return response;
  }

  async broadcast(data: BroadcastNotificationDto): Promise<NotificationSendResult> {
    const client = getApiClient();
    const response = await client.post<NotificationSendResult>(`${BASE_URL}/broadcast`, data);
    return response;
  }

  // ============ My Notifications (Inbox) ============
  async getMyNotifications(page = 1, limit = 20): Promise<PaginatedNotifications> {
    const client = getApiClient();
    const response = await client.get<PaginatedNotifications>(`${BASE_URL}/my?page=${page}&limit=${limit}`);
    return response;
  }

  async getUnreadNotifications(): Promise<MasterNotification[]> {
    const client = getApiClient();
    const response = await client.get<MasterNotification[]>(`${BASE_URL}/my/unread`);
    return response;
  }

  async getUnreadCount(): Promise<number> {
    const client = getApiClient();
    const response = await client.get<UnreadCountResponse>(`${BASE_URL}/my/unread/count`);
    return response.count;
  }

  async markAsRead(id: string): Promise<void> {
    const client = getApiClient();
    await client.post<void>(`${BASE_URL}/my/${id}/read`, {});
  }

  async markAllAsRead(): Promise<number> {
    const client = getApiClient();
    const response = await client.post<MarkAllReadResponse>(`${BASE_URL}/my/read-all`, {});
    return response.marked;
  }

  async deleteNotification(id: string): Promise<void> {
    const client = getApiClient();
    await client.delete(`${BASE_URL}/my/${id}`);
  }
}
